import { useNavigate, useParams, Navigate, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, ClipboardCheck, FileCode, Video } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { NextStepBar, NextStepChip } from "@/components/app/NextStepBar";
import { JobStatusBadge, StatusChip, analysisLabel } from "@/components/app/StatusChip";
import { OverviewTab } from "@/pages/job/OverviewTab";
import { JobSettingsTab } from "@/pages/job/JobSettingsTab";
import { CandidatesTab } from "@/pages/job/CandidatesTab";
import { PipelineTab } from "@/pages/job/PipelineTab";
import { AssessmentsTab } from "@/pages/job/AssessmentsTab";
import { WorkSamplesTab } from "@/pages/job/ws/WorkSamplesTab";
import { OverviewCharts } from "@/pages/job/OverviewCharts";
import { CalibrationPanel } from "@/pages/job/CalibrationPanel";
import { FinalRanking } from "@/pages/job/ranking/FinalRanking";
import { TabBar } from "@/components/motion/TabBar";
import { CopyLinkButton, careersUrl } from "@/components/app/CopyLinkButton";
import { deleteJob } from "@/lib/deleteItem";
import { Trash2 } from "lucide-react";
import { StepStrip } from "@/components/app/StepStrip";
import { WorkspaceStep } from "@/pages/job/WorkspaceStep";
import { useMiniTour } from "@/components/app/miniTours";
import { QuestionBankTab } from "@/pages/job/QuestionBankTab";
import { pqeText } from "@/pages/job/LegalFields";

const EMPTY = {
  interviews: [Video, "No interviews yet", "AI interview invitations, recordings and scores for this job will appear here."],
};

function tabsFor(job, isStaff) {
  const t = [["workspace", "Workspace"], ["overview", "Overview"], ["candidates", "All candidates"], ["pipeline", "Pipeline"], ["assessments", "Assessments"]];
  if (job.work_sample_config?.enabled) t.push(["work-samples", "Work samples"]);
  t.push(["question-bank", "Question bank"], ["interviews", "Interviews"], ["final-ranking", "Final ranking"]);
  if (isStaff) t.push(["settings", "Settings"]);
  return t;
}

function nextStep(job, nav, setStatus, ranked) {
  if (job.status === "draft" && !job.requirements_confirmed) return { text: "Finish setting up this job", label: "Continue setup", onAction: () => nav(`/app/jobs/new?draft=${job.id}`) };
  if (job.status === "draft") return { text: "Publish this job to start receiving candidates", label: "Review and publish", onAction: () => nav(`/app/jobs/new?draft=${job.id}&step=3`) };
  if (job.status === "paused") return { text: "This job is paused. Reopen it to keep screening.", label: "Reopen job", onAction: () => setStatus("open") };
  if (job.status === "closed") return { text: "This job is closed. Review the final ranking.", label: "Final ranking", onAction: () => nav(`/app/jobs/${job.id}/final-ranking`) };
  if (job.candidate_count > 0) {
    const rows = ranked?.rows || [];
    const screened = rows.filter((r) => r.resume_match != null).length;
    const shortlisted = rows.filter((r) => r.shortlisted).length;
    if (rows.length && screened < rows.length) return { text: `${screened} of ${rows.length} resumes screened — scoring continues in the background`, label: "View ranking", onAction: () => nav(`/app/jobs/${job.id}/candidates`) };
    if (!shortlisted) return { text: `${screened} resumes screened — review and shortlist`, label: "Review ranking", onAction: () => nav(`/app/jobs/${job.id}/candidates`) };
    return { text: `${shortlisted} candidate(s) shortlisted — track them in the pipeline`, hint: "Send the work-style assessment from the Assessments tab.", label: "Open assessments", onAction: () => nav(`/app/jobs/${job.id}/assessments`) };
  }
  return { text: "Add resumes to start screening", hint: "Upload resumes in bulk or share the careers page.", label: "Add resumes", onAction: () => nav(`/app/jobs/${job.id}/candidates`) };
}

export default function JobDetail() {
  const { id, tab } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isStaff = STAFF.includes(user.role);
  const { data: job, isLoading, isError } = useQuery({ queryKey: ["job", id], queryFn: () => get(`/jobs/${id}`) });
  const { data: ranked } = useQuery({ queryKey: ["ranked", id], queryFn: () => get(`/jobs/${id}/ranked`), enabled: !!job && job.status !== "draft" });
  const { data: ws } = useQuery({ queryKey: ["workspace", id], queryFn: () => get(`/jobs/${id}/workspace`), enabled: !!job && job.status !== "draft", refetchInterval: 15000 });
  const [params] = useSearchParams();
  const firstOpen = ws?.steps.find((s) => s.needs > 0)?.n || 1;
  const wsStep = Number(params.get("step")) || firstOpen;
  useMiniTour("workspace", !!ws && tab === "workspace" && wsStep === 3);

  const setStatus = async (status) => {
    try {
      await api.post(`/jobs/${id}/status`, { status });
      await qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success(`Job ${status === "open" ? "reopened" : status}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (isLoading) return <SkeletonRows rows={6} />;
  if (isError || !job) return <EmptyState testId="job-not-found" title="Job not found" text="It may have been deleted." action={<Button asChild variant="outline"><Link to="/app/jobs">Back to jobs</Link></Button>} />;
  const tabs = tabsFor(job, isStaff);
  if (!tabs.some(([k]) => k === tab)) return <Navigate to={`/app/jobs/${id}/workspace`} replace />;
  const step = nextStep(job, nav, setStatus, ranked);

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <Link to="/app/jobs" className="text-sm text-ink-muted hover:text-ink">Jobs</Link>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="job-detail-title">{job.title || "Untitled job"}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-ink-muted">
            <JobStatusBadge status={job.status} testId="job-detail-status" />
            {job.status === "open" && <CopyLinkButton url={careersUrl(job)} size="sm" testId="copy-careers-link" />}
            <StatusChip status={analysisLabel(job.analysis_status)} />
            {[job.location, job.law_firm].filter(Boolean).map((x) => <span key={x} data-testid="job-header-meta">· {x}</span>)}
          </div>
          {(job.practice_area || pqeText(job) || job.bar_required) && (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="job-legal-chips">
              {job.practice_area && <span className="rounded-full bg-brand-tint px-2.5 py-0.5 font-medium text-brand" data-testid="job-practice-area">{job.practice_area}</span>}
              {pqeText(job) && <span className="rounded-full bg-subtle px-2.5 py-0.5 text-ink-secondary ring-1 ring-line" data-testid="job-pqe">{pqeText(job)}</span>}
              {job.bar_required && <span className="rounded-full bg-subtle px-2.5 py-0.5 text-ink-secondary ring-1 ring-line" data-testid="job-bar-required">Bar enrolment required</span>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <NextStepChip />
        {isStaff && <Button variant="outline" size="sm" className="text-risk hover:text-risk" data-testid={`delete-job-${job.id}`}
          onClick={() => deleteJob(job, () => { qc.removeQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); nav("/app/jobs"); })}><Trash2 size={14} /> Delete job</Button>}
        {isStaff && job.status !== "draft" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" data-testid="job-status-menu"><MoreHorizontal size={16} /> Status</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-[6px]">
              {job.status !== "open" && <DropdownMenuItem data-testid="job-status-open" onClick={() => setStatus("open")}>Reopen</DropdownMenuItem>}
              {job.status === "open" && <DropdownMenuItem data-testid="job-status-pause" onClick={() => setStatus("paused")}>Pause</DropdownMenuItem>}
              {job.status !== "closed" && <DropdownMenuItem data-testid="job-status-close" onClick={() => setStatus("closed")}>Close</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
      </div>
      {tab !== "workspace" && <NextStepBar text={step.text} hint={step.hint} actionLabel={isStaff ? step.label : undefined} onAction={step.onAction} />}
      {job.status !== "draft" && <StepStrip steps={ws?.steps} active={tab === "workspace" ? wsStep : null} onPick={(n) => nav(`/app/jobs/${id}/workspace?step=${n}`)} />}
      <TabBar className="text-sm" tabs={tabs} active={tab} hrefFor={(k) => `/app/jobs/${id}/${k}`} testIdFor={(k) => `job-tab-${k}`} testId="job-tabs" />
      <div key={tab} className="tl-page">
        {tab === "workspace" && <WorkspaceStep job={job} data={ws} step={wsStep} staff={isStaff} />}
        {tab === "overview" && <><OverviewTab job={job} /><div className="mt-6"><OverviewCharts job={job} /></div></>}
        {tab === "candidates" && <CandidatesTab job={job} />}
        {tab === "pipeline" && <PipelineTab job={job} />}
        {tab === "assessments" && <AssessmentsTab job={job} />}
        {tab === "work-samples" && job.work_sample_config?.enabled && <WorkSamplesTab job={job} />}
        {tab === "settings" && user.role === "super_admin" && <div className="mb-6"><CalibrationPanel job={job} /></div>}
        {tab === "settings" && <JobSettingsTab job={job} />}
        {tab === "final-ranking" && <FinalRanking job={job} />}
        {tab === "question-bank" && <QuestionBankTab job={job} staff={isStaff} />}
        {EMPTY[tab] && <EmptyState testId={`job-tab-empty-${tab}`} icon={EMPTY[tab][0]} title={EMPTY[tab][1]} text={EMPTY[tab][2]} />}
      </div>
    </div>
  );
}
